import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useExpanded, useFlexLayout, useTable } from 'react-table';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import HelpArticleLink from '../../../../components/help-article-link';
import { SimpleModal } from '../../../../components/simple-modal/simple-modal';
import UserDateTimezoneFormat from '../../../../components/user-date-timezone-format';
import { INVENTORY_SYNC_TARGETS_FAQ_CLICK } from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import { linkIDs } from '../../../../services/link-service/links';
import { InventoryConnectionJobStatusEnum } from '../enums';
import useConsolidatedInventorySources from '../use-consolidated-inventory-sources';
import { JobStatus } from './job-status';
import { messages } from './messages';
import styles from './sync-details-modal.module.less';

const noSyncData = '-';

export const SkuCell = ({ row }) => {
  return row?.values?.sku;
};

export const QuantityCell = ({ row }) => {
  return row?.values?.totalAvailable;
};

export const ErrorMessageCell = ({ row }) => {
  return row?.values?.errorMessage;
};

SkuCell.propTypes = {
  row: PropTypes.shape({
    values: PropTypes.shape({
      sku: PropTypes.string,
    }),
  }).isRequired,
};

QuantityCell.propTypes = {
  row: PropTypes.shape({
    values: PropTypes.shape({
      totalAvailable: PropTypes.number,
    }),
  }).isRequired,
};

ErrorMessageCell.propTypes = {
  row: PropTypes.shape({
    values: PropTypes.shape({
      errorMessage: PropTypes.string,
    }),
  }).isRequired,
};

export const createSkuColDefinition = createColumnDefinitionBuilder({
  accessor: 'sku',
  Header: <FormattedMessage {...messages.sku} />,
  Cell: SkuCell,
});

export const createQuantityColDefinition = createColumnDefinitionBuilder({
  accessor: 'totalAvailable',
  Header: <FormattedMessage {...messages.quantity} />,
  Cell: QuantityCell,
});

export const createErrorMessageColDefinition = createColumnDefinitionBuilder({
  accessor: 'errorMessage',
  Header: <FormattedMessage {...messages.errorMessage} />,
  Cell: ErrorMessageCell,
});

const columns = [createSkuColDefinition(), createQuantityColDefinition(), createErrorMessageColDefinition()];

export const SyncDetailsModal = ({ onClose, connectionId, targetName, ...rest }) => {
  const { loading: isLoading, connections, fetchConsolidatedInventoryConnections } = useConsolidatedInventorySources();
  const { formatMessage } = useIntl();
  useEffect(() => {
    fetchConsolidatedInventoryConnections();
  }, [fetchConsolidatedInventoryConnections]);

  const connection = connections.find((c) => c.connectionId === connectionId);

  const syncInfo = useMemo(() => {
    try {
      return JSON.parse(connection?.lastJobInfo);
    } catch {
      return {};
    }
  }, [connection?.lastJobInfo]);

  const data = useMemo(() => {
    const jobResult = syncInfo.result;

    if (!jobResult) {
      return [];
    }

    const tableData = [];
    if (Array.isArray(jobResult.skus_with_errors)) {
      tableData.push(...jobResult.skus_with_errors);
    }
    if (Array.isArray(jobResult.pushed_skus)) {
      tableData.push(...jobResult.pushed_skus);
    }

    return tableData;
  }, [syncInfo]);

  const skusUpdated = syncInfo.result?.total_skus_updated || 0;
  const skusWithErrors = syncInfo.result?.total_skus_with_errors || 0;

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useFlexLayout,
    useExpanded,
  );

  const jobTheme = useMemo(() => {
    let theme = null;
    switch (syncInfo.status) {
      case InventoryConnectionJobStatusEnum.failed:
        theme = 'error';
        break;
      case InventoryConnectionJobStatusEnum.succeeded:
        theme = 'success';
        break;
      case InventoryConnectionJobStatusEnum.running:
        theme = 'running';
        break;
      case InventoryConnectionJobStatusEnum.enqueued:
        theme = 'enqueued';
        break;
      default:
        break;
    }

    return theme;
  }, [syncInfo.status]);

  return (
    <SimpleModal headerMessage={formatMessage(messages.modalHeader, { targetName })} onClose={onClose} size='medium' {...rest}>
      <div className={styles['modal-content']}>
        {isLoading ? (
          <LoadingIndicator className={styles['loading-indicator']} />
        ) : (
          <>
            <div className={styles['summarize-info']}>
              <div className={styles['summarize-info-item']}>
                <span className={styles['summarize-info-header']}>
                  <FormattedMessage {...messages.lastSync} />
                </span>
                <span className={styles['summarize-info-value']}>
                  {syncInfo.createdAt ? (
                    <UserDateTimezoneFormat dateTime={syncInfo.createdAt} showTime spaceBetweenTimeAndZone />
                  ) : (
                    noSyncData
                  )}
                </span>
              </div>
              <div className={styles['summarize-info-item']}>
                <span className={styles['summarize-info-header']}>
                  <FormattedMessage {...messages.skusUpdated} />
                </span>
                <span className={styles['summarize-info-value']}>{skusUpdated}</span>
              </div>
            </div>
            <div className={classNames(styles['summarize-info'], styles['with-top-margin'])}>
              <div className={styles['summarize-info-item']}>
                <span className={styles['summarize-info-header']}>
                  <FormattedMessage {...messages.jobStatus} />
                </span>
                <span className={styles['summarize-info-value']}>{jobTheme ? <JobStatus theme={jobTheme} /> : noSyncData}</span>
              </div>
              <div className={styles['summarize-info-item']}>
                <span className={styles['summarize-info-header']}>
                  <FormattedMessage {...messages.errors} />
                </span>
                <span className={styles['summarize-info-value']}>{skusWithErrors}</span>
              </div>
            </div>
            {skusWithErrors > 0 && (
              <BannerMessageV2
                className={styles.banner}
                content={
                  <>
                    <FormattedMessage {...messages.bannerInfo} />
                    <Button theme='link'>
                      <FormattedMessage
                        {...messages.bannerLink}
                        values={{
                          a: (chunks) => (
                            <HelpArticleLink
                              articleId={linkIDs.InventorySyncTargetsFAQ}
                              onClick={() =>
                                Segment.trackEvent(INVENTORY_SYNC_TARGETS_FAQ_CLICK, {
                                  syncTargetName: targetName,
                                  lastSyncAt: syncInfo.createdAt,
                                  totalSkusWithErrors: syncInfo.result?.total_skus_with_errors,
                                  totalSkusUpdated: syncInfo.result?.total_skus_updated,
                                })
                              }
                            >
                              {chunks}
                            </HelpArticleLink>
                          ),
                        }}
                      />
                    </Button>
                  </>
                }
                status={BannerStatus.Info}
              />
            )}
            {data.length > 0 && (
              <ReactTableWrapper
                className={styles['table-body']}
                getTableBodyProps={getTableBodyProps}
                getTableProps={getTableProps}
                headerGroups={headerGroups}
                headerRowClassName={styles['table-column-header']}
                prepareRow={prepareRow}
                rows={rows}
              />
            )}
          </>
        )}
      </div>
    </SimpleModal>
  );
};

SyncDetailsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  targetName: PropTypes.string.isRequired,
  connectionId: PropTypes.string.isRequired,
};
